import React, { useState, useMemo } from 'react';
import { SignedIn, SignedOut, SignInButton, useUser, useClerk } from '@clerk/clerk-react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo-wallet.png';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Avatar,
  Box,
  Typography
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import QrCodeIcon from '@mui/icons-material/QrCode';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LogoutIcon from '@mui/icons-material/Logout';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ForumIcon from '@mui/icons-material/Forum';
import ArticleIcon from '@mui/icons-material/Article';
import LockIcon from '@mui/icons-material/Lock';
import GroupIcon from '@mui/icons-material/Group';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSubscription } from '../hooks/useSubscription';
import { IntentModeSwitcher } from './onboarding';
import { useCurrentIntent, useIntentStore } from '../stores/intentStore';
import { HelpTourButton } from './HelpTourButton';

export const Header = () => {
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { hasAnalyticsAccess, tier, isMember, isProOrHigher } = useSubscription();
  const { intent } = useIntentStore();
  const isEngagementUser = intent === 'engagement';

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
  const [resourcesAnchorEl, setResourcesAnchorEl] = useState<null | HTMLElement>(null);
  const [productsAnchorEl, setProductsAnchorEl] = useState<null | HTMLElement>(null);

  const userInitial = useMemo(() => {
    if (!user) return '?';
    const source =
      user.fullName ||
      user.firstName ||
      user.lastName ||
      user.username ||
      user.emailAddresses?.[0]?.emailAddress ||
      '';
    return source ? source.charAt(0).toUpperCase() : '?';
  }, [user]);

  const userLabel = useMemo(() => {
    if (!user) return 'My Profile';
    return user.fullName || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || 'My Profile';
  }, [user]);

  const userEmail = useMemo(() => {
    if (!user) return '';
    return (
      user.primaryEmailAddress?.emailAddress ||
      user.emailAddresses?.[0]?.emailAddress ||
      ''
    );
  }, [user]);

  const isProfileMenuOpen = Boolean(profileAnchorEl);
  const isResourcesMenuOpen = Boolean(resourcesAnchorEl);
  const isProductsMenuOpen = Boolean(productsAnchorEl);

  const resourceMenuItems = useMemo(
    () => [
      {
        to: '/support',
        label: 'Support Center',
        description: 'How-to guides and troubleshooting',
        icon: <SupportAgentIcon fontSize="small" />
      },
      {
        to: '/feedback-roadmap',
        label: 'Feedback & Roadmap',
        description: 'See what’s coming next',
        icon: <ForumIcon fontSize="small" />
      },
      {
        to: '/docs/automation/webhook-zapier',
        label: 'Automation Guides',
        description: 'Zapier, Make & custom webhook playbooks',
        icon: <QrCodeIcon fontSize="small" />
      },
      {
        to: '/blog',
        label: 'Blog',
        description: 'Tips, updates, and inspiration',
        icon: <ArticleIcon fontSize="small" />
      }
    ],
    []
  );

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleResourcesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setResourcesAnchorEl(event.currentTarget);
  };

  const handleResourcesMenuClose = () => {
    setResourcesAnchorEl(null);
  };

  const handleProductsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProductsAnchorEl(event.currentTarget);
  };

  const handleProductsMenuClose = () => {
    setProductsAnchorEl(null);
  };

  const handleNavigateProfile = () => {
    handleMenuClose();
    closeMobileMenu();
    navigate('/my-profile');
  };

  const handleNavigateDigitalCard = () => {
    // Digital cards now available for all tiers (Free, Starter, Pro+)
    handleMenuClose();
    closeMobileMenu();
    navigate('/digital-card');
  };

  const handleNavigateSubscription = () => {
    handleMenuClose();
    closeMobileMenu();
    navigate('/manage-subscription');
  };

  const handleNavigateTeamManagement = () => {
    // Business tier: Navigate to pricing instead of team management page
    if (tier !== 'business' && tier !== 'enterprise') {
      navigate('/pricing?highlight=business');
      setProfileAnchorEl(null); // Close menu
      return;
    }
    handleMenuClose();
    closeMobileMenu();
    navigate('/team-management');
  };

  const handleSignOutClick = async () => {
    handleMenuClose();
    closeMobileMenu();
    try {
      await signOut({ redirectUrl: '/' });
    } catch (error) {
      console.error('[Header] Sign out failed:', error);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Brand */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
              <img src={logo} alt="SignatureForward" className="h-8 w-auto" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {/* Guest Navigation */}
            <SignedOut>
              {/* Products dropdown */}
              <button
                onClick={handleProductsMenuOpen}
                className="flex items-center text-[#1f2a44] hover:text-[#1d4ed8] font-medium transition-colors"
                aria-controls={isProductsMenuOpen ? 'products-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isProductsMenuOpen ? 'true' : undefined}
              >
                Products
                <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.25 }} />
              </button>
              <Menu
                id="products-menu"
                anchorEl={productsAnchorEl}
                open={isProductsMenuOpen}
                onClose={handleProductsMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 220,
                    borderRadius: '0.5rem',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    py: 0.5
                  }
                }}
              >
                <MenuItem
                  onClick={() => { handleProductsMenuClose(); navigate('/generator'); }}
                  sx={{ py: 1.25, px: 2, '&:hover': { backgroundColor: '#f9fafb' } }}
                >
                  <ListItemIcon><EmailIcon fontSize="small" sx={{ color: '#e11d48' }} /></ListItemIcon>
                  <ListItemText primary="Email Signatures" primaryTypographyProps={{ fontSize: '0.875rem', color: '#374151' }} />
                </MenuItem>
                <MenuItem
                  onClick={() => { handleProductsMenuClose(); navigate('/digital-business-cards'); }}
                  sx={{ py: 1.25, px: 2, '&:hover': { backgroundColor: '#f9fafb' } }}
                >
                  <ListItemIcon><BadgeIcon fontSize="small" sx={{ color: '#e11d48' }} /></ListItemIcon>
                  <ListItemText primary="Digital Business Cards" primaryTypographyProps={{ fontSize: '0.875rem', color: '#374151' }} />
                </MenuItem>
                <MenuItem
                  onClick={() => { handleProductsMenuClose(); navigate('/wallet-passes'); }}
                  sx={{ py: 1.25, px: 2, '&:hover': { backgroundColor: '#f9fafb' } }}
                >
                  <ListItemIcon><AccountBalanceWalletIcon fontSize="small" sx={{ color: '#e11d48' }} /></ListItemIcon>
                  <ListItemText primary="Wallet Passes" primaryTypographyProps={{ fontSize: '0.875rem', color: '#374151' }} />
                </MenuItem>
                <MenuItem
                  onClick={() => { handleProductsMenuClose(); navigate('/loyalty'); }}
                  sx={{ py: 1.25, px: 2, '&:hover': { backgroundColor: '#f9fafb' } }}
                >
                  <ListItemIcon><LoyaltyIcon fontSize="small" sx={{ color: '#e11d48' }} /></ListItemIcon>
                  <ListItemText primary="Loyalty Programs" primaryTypographyProps={{ fontSize: '0.875rem', color: '#374151' }} />
                </MenuItem>
              </Menu>
              <Link
                to="/pricing"
                className="text-[#1f2a44] hover:text-[#1d4ed8] font-medium transition-colors"
              >
                Pricing
              </Link>
              <Link
                to="/blog"
                className="text-[#1f2a44] hover:text-[#1d4ed8] font-medium transition-colors"
              >
                Blog
              </Link>

              {/* Primary CTA (only ONE colored button) */}
              <SignInButton mode="modal">
                <button className="bg-[#e11d48] hover:bg-[#be123c] text-white px-5 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all">
                  Get Started Free
                </button>
              </SignInButton>
            </SignedOut>

            {/* Signed-in Navigation */}
            <SignedIn>
              {/* 1. Dashboard = your main hub (keep this name) */}
              <Link 
                to="/dashboard" 
                className="text-[#1f2a44] hover:text-[#1d4ed8] font-medium transition-colors"
              >
                Dashboard
              </Link>
              {/* Campaigns - prominent for engagement users */}
              {isEngagementUser && (
                <Link 
                  to="/dashboard?tab=campaigns" 
                  className="text-[#e11d48] hover:text-[#be123c] font-semibold transition-colors flex items-center gap-1"
                >
                  🎟️ Campaigns
                </Link>
              )}
              {/* Intent Mode Switcher - shows current mode with ability to switch */}
              <IntentModeSwitcher variant="compact" />
              {/* Help Tour Button - guided tours for current page */}
              <HelpTourButton />
              {/* 2. Digital Cards – always visible */}
              <Link
                to="/digital-card"
                className="text-[#1f2a44] hover:text-[#1d4ed8] font-medium transition-colors"
              >
                Digital Cards
              </Link>
              {/* 3. Analytics – show for Starter+ users */}
              {hasAnalyticsAccess && (
                <Link
                  to="/analytics"
                  className="text-[#1f2a44] hover:text-[#1d4ed8] font-medium transition-colors"
                >
                  Analytics
                </Link>
              )}
              {/* 4. Team Management – only show when on Business/Team plan or higher */}
              {(tier === 'business' || tier === 'enterprise') && (
                <Link 
                  to="/team-management" 
                  className="text-[#1f2a44] hover:text-[#1d4ed8] font-medium transition-colors"
                >
                  Team
                </Link>
              )}
              {/* Integrations (Shopify) – show for Pro+ users */}
              {isProOrHigher && (
                <Link
                  to="/integrations/shopify"
                  className="text-[#1f2a44] hover:text-[#1d4ed8] font-medium transition-colors"
                >
                  Integrations
                </Link>
              )}
              {/* 4. Pricing – keep it (tons of logged-in users upgrade from here) */}
              <Link 
                to="/pricing" 
                className="text-[#1f2a44] hover:text-[#1d4ed8] font-medium transition-colors"
              >
                Pricing
              </Link>

              {/* Right side: user menu (clean, modern avatar dropdown) */}
              <div className="relative">
                <button
                  type="button"
                  onClick={handleMenuOpen}
                  onMouseEnter={handleMenuOpen}
                  aria-controls={isProfileMenuOpen ? 'profile-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={isProfileMenuOpen ? 'true' : undefined}
                  className="flex items-center space-x-3 rounded-full focus:outline-none focus:ring-2 focus:ring-[#e11d48] focus:ring-offset-2 transition-all"
                  aria-label="User menu"
                >
                  <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-[#e11d48] ring-offset-2">
                    {user?.imageUrl ? (
                      <img 
                        src={user.imageUrl} 
                        alt={userLabel} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#1f2a44] flex items-center justify-center text-white font-semibold text-sm">
                        {userInitial}
                      </div>
                    )}
                  </div>
                  <span className="hidden lg:inline font-medium text-[#1f2a44]">
                    {userLabel}
                  </span>
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>
                <Menu
                  id="profile-menu"
                  anchorEl={profileAnchorEl}
                  open={isProfileMenuOpen}
                  onClose={handleMenuClose}
                  MenuListProps={{
                    onMouseLeave: handleMenuClose
                  }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 192,
                      borderRadius: '0.5rem',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      py: 0.25
                    }
                  }}
                >
                  <MenuItem 
                    onClick={handleNavigateProfile}
                    sx={{ 
                      py: 1,
                      px: 2,
                      '&:hover': { backgroundColor: '#f9fafb' }
                    }}
                  >
                    <ListItemText 
                      primary="Account" 
                      primaryTypographyProps={{ 
                        fontSize: '0.875rem',
                        color: '#374151'
                      }}
                    />
                  </MenuItem>
                  <MenuItem 
                    onClick={handleNavigateSubscription}
                    sx={{ 
                      py: 1,
                      px: 2,
                      '&:hover': { backgroundColor: '#f9fafb' }
                    }}
                  >
                    <ListItemText 
                      primary="Billing" 
                      primaryTypographyProps={{ 
                        fontSize: '0.875rem',
                        color: '#374151'
                      }}
                    />
                  </MenuItem>
                  {/* Team Management link duplicated here for Business+ users */}
                  {(tier === 'business' || tier === 'enterprise') && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <MenuItem 
                        onClick={handleNavigateTeamManagement}
                        sx={{ 
                          py: 1,
                          px: 2,
                          '&:hover': { backgroundColor: '#f9fafb' }
                        }}
                      >
                        <ListItemText 
                          primary="Team Management" 
                          primaryTypographyProps={{ 
                            fontSize: '0.875rem',
                            color: '#374151'
                          }}
                        />
                      </MenuItem>
                    </>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem 
                    onClick={handleSignOutClick}
                    sx={{ 
                      py: 1,
                      px: 2,
                      '&:hover': { backgroundColor: '#f9fafb' }
                    }}
                  >
                    <ListItemText 
                      primary="Sign out" 
                      primaryTypographyProps={{ 
                        fontSize: '0.875rem',
                        color: '#374151'
                      }}
                    />
                  </MenuItem>
                </Menu>
              </div>
            </SignedIn>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-[#1f2a44] hover:text-[#1d4ed8]"
              aria-label="Toggle mobile menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div className={`md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
          <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200 shadow-lg">
            {/* Guest Navigation */}
            <SignedOut>
              <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Products</div>
              <Link
                to="/generator"
                className="block w-full text-left text-[#1f2a44] hover:text-[#1d4ed8] px-4 py-3 rounded-md transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                Email Signatures
              </Link>
              <Link
                to="/digital-business-cards"
                className="block w-full text-left text-[#1f2a44] hover:text-[#1d4ed8] px-4 py-3 rounded-md transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                Digital Business Cards
              </Link>
              <Link
                to="/wallet-passes"
                className="block w-full text-left text-[#1f2a44] hover:text-[#1d4ed8] px-4 py-3 rounded-md transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                Wallet Passes
              </Link>
              <Link
                to="/loyalty"
                className="block w-full text-left text-[#1f2a44] hover:text-[#1d4ed8] px-4 py-3 rounded-md transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                Loyalty Programs
              </Link>
              <div className="border-t border-gray-200 my-2"></div>
              <Link
                to="/pricing"
                className="block w-full text-left text-[#1f2a44] hover:text-[#1d4ed8] px-4 py-3 rounded-md transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                Pricing
              </Link>
              <Link
                to="/blog"
                className="block w-full text-left text-[#1f2a44] hover:text-[#1d4ed8] px-4 py-3 rounded-md transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                Blog
              </Link>
              <SignInButton mode="modal">
                <button 
                  className="block w-full text-left bg-[#e11d48] hover:bg-[#be123c] text-white px-4 py-3 rounded-lg font-semibold shadow-md transition-all"
                  onClick={closeMobileMenu}
                >
                  Get Started Free
                </button>
              </SignInButton>
            </SignedOut>

            {/* Signed-in Navigation */}
            <SignedIn>
              <Link 
                to="/dashboard" 
                className="block w-full text-left text-[#1f2a44] hover:text-[#1d4ed8] px-4 py-3 rounded-md transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                Dashboard
              </Link>
              {/* Campaigns - prominent for engagement users */}
              {isEngagementUser && (
                <Link 
                  to="/dashboard?tab=campaigns" 
                  className="block w-full text-left text-[#e11d48] hover:text-[#be123c] px-4 py-3 rounded-md transition-colors font-semibold"
                  onClick={closeMobileMenu}
                >
                  🎟️ Campaigns
                </Link>
              )}
              {hasAnalyticsAccess && (
                <Link 
                  to="/analytics" 
                  className="block w-full text-left text-[#1f2a44] hover:text-[#1d4ed8] px-4 py-3 rounded-md transition-colors font-medium"
                  onClick={closeMobileMenu}
                >
                  Analytics
                </Link>
              )}
              <div className="border-t border-gray-200 my-2"></div>
              <button
                className="block w-full text-left text-[#1f2a44] hover:text-[#1d4ed8] px-4 py-3 rounded-md transition-colors font-medium"
                onClick={() => {
                  closeMobileMenu();
                  navigate('/my-profile');
                }}
              >
                My Profile
              </button>
              <button
                className="block w-full text-left text-[#1f2a44] hover:text-[#1d4ed8] px-4 py-3 rounded-md transition-colors font-medium"
                onClick={() => {
                  closeMobileMenu();
                  navigate('/digital-card');
                }}
              >
                Digital Card
              </button>
              <button
                className="block w-full text-left text-[#1f2a44] hover:text-[#1d4ed8] px-4 py-3 rounded-md transition-colors font-medium"
                onClick={() => {
                  closeMobileMenu();
                  navigate('/manage-subscription');
                }}
              >
                Manage Subscription
              </button>
              <div className="border-t border-gray-200 my-2"></div>
              <button
                className="block w-full text-left bg-[#e11d48] hover:bg-[#be123c] text-white px-4 py-3 rounded-lg font-semibold transition-all"
                onClick={handleSignOutClick}
              >
                Sign Out
              </button>
            </SignedIn>
          </div>
        </div>
      </div>
    </header>
  );
}; 